import * as React from "react";

import { Combobox } from "@/shared/foundation/primitives";

import styles from "./MetricDefinitionsTable.module.css";

/** One row of the unit list: a unit to set, or clearing the unit. */
type UnitOption =
  { kind: "unit"; unit: string; count: number } | { kind: "clear" };

interface UnitGroup {
  label: string | null;
  items: UnitOption[];
}

const CLEAR: UnitOption = { kind: "clear" };

export interface UnitEditorProps {
  /** The definition's name, for the field's accessible name. */
  name: string;
  /** The unit now set, or `null`. */
  unit: string | null;
  /** How many of the org's definitions use each unit. */
  unitCounts: ReadonlyMap<string, number>;
  /**
   * Called once, with the unit to save (`null` clears it), or with
   * `undefined` when the edit is cancelled.
   */
  onDone: (unit: string | null | undefined) => void;
  /** Shown while the save runs: the field keeps the new text but takes no input. */
  saving: boolean;
}

/**
 * The Unit cell while it's being edited: a combobox that opens at once,
 * listing the units the org uses that match what's typed (with how many
 * metrics use each), then the rest, then "Clear unit". Enter saves what is
 * typed unless a unit in the list is highlighted; clicking away saves too,
 * and Esc cancels.
 */
export function UnitEditor({
  name,
  unit,
  unitCounts,
  onDone,
  saving,
}: UnitEditorProps) {
  const [query, setQuery] = React.useState(unit ?? "");
  const done = React.useRef(false);
  const finish = (next: string | null | undefined) => {
    if (done.current) {
      return;
    }
    done.current = true;
    onDone(next === undefined ? undefined : next?.trim() || null);
  };

  const groups = React.useMemo<UnitGroup[]>(() => {
    const needle = query.trim().toLowerCase();
    const options = [...unitCounts].map(([value, count]): UnitOption => ({
      kind: "unit",
      unit: value,
      count,
    }));
    const rank = (option: UnitOption) => {
      const value = option.kind === "unit" ? option.unit.toLowerCase() : "";
      if (value === needle) {
        return 0;
      }
      return value.startsWith(needle) ? 1 : 2;
    };
    const matching = options
      .filter(
        (option) =>
          option.kind === "unit" && option.unit.toLowerCase().includes(needle),
      )
      .sort((a, b) => rank(a) - rank(b));
    const others = options.filter((option) => !matching.includes(option));
    return [
      { label: "Used in this org", items: matching },
      { label: "Other units", items: others },
      { label: null, items: [CLEAR] },
    ].filter((group) => group.items.length > 0);
  }, [query, unitCounts]);

  const current = React.useMemo<UnitOption | null>(
    () =>
      unit === null
        ? null
        : { kind: "unit", unit, count: unitCounts.get(unit) ?? 0 },
    [unit, unitCounts],
  );

  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <div
      className={styles.unitEditor}
      // Enter with nothing in the list highlighted saves the typed text,
      // which the list needn't hold: a unit is free text. The Enter that
      // confirms an IME composition is the IME's: Chrome sends it before the
      // composed text reaches `query`.
      onKeyDownCapture={(event) => {
        if (
          event.key === "Enter" &&
          !event.nativeEvent.isComposing &&
          event.keyCode !== 229 &&
          !inputRef.current?.getAttribute("aria-activedescendant")
        ) {
          event.preventDefault();
          event.stopPropagation();
          finish(query);
        }
      }}
    >
      <Combobox<UnitOption>
        items={groups}
        value={current}
        filter={null}
        defaultOpen
        disabled={saving}
        itemToStringLabel={(option) =>
          option.kind === "unit" ? option.unit : ""
        }
        isItemEqualToValue={(option, value) =>
          option.kind === value.kind &&
          (option.kind !== "unit" ||
            (value.kind === "unit" && option.unit === value.unit))
        }
        onInputValueChange={setQuery}
        onValueChange={(option) => {
          if (option) {
            finish(option.kind === "clear" ? null : option.unit);
          }
        }}
        onOpenChange={(open, { reason }) => {
          if (open) {
            return;
          }
          if (reason === "escape-key") {
            finish(undefined);
          } else if (reason === "outside-press" || reason === "focus-out") {
            finish(query);
          }
        }}
      >
        <Combobox.TriggerInput
          ref={inputRef}
          aria-label={`Unit of ${name}`}
          autoFocus
          className={styles.unitField}
        />
        <Combobox.Content align="start" minWidth="200px">
          <Combobox.List>
            {(group: UnitGroup) => (
              <Combobox.Group
                key={group.label ?? "clear"}
                items={group.items}
                className={group.label === null ? styles.clearGroup : undefined}
              >
                {group.label !== null && (
                  <Combobox.GroupLabel className={styles.unitGroupLabel}>
                    {group.label}
                  </Combobox.GroupLabel>
                )}
                <Combobox.Collection>
                  {(option: UnitOption) =>
                    option.kind === "clear" ? (
                      <Combobox.Item key="clear" value={option}>
                        Clear unit
                      </Combobox.Item>
                    ) : (
                      <Combobox.Item
                        key={option.unit}
                        value={option}
                        className={styles.unitOption}
                      >
                        <span className={styles.unitText}>{option.unit}</span>
                        <span className={styles.unitCount}>
                          {option.count === 1
                            ? "1 metric"
                            : `${option.count} metrics`}
                        </span>
                      </Combobox.Item>
                    )
                  }
                </Combobox.Collection>
              </Combobox.Group>
            )}
          </Combobox.List>
        </Combobox.Content>
      </Combobox>
    </div>
  );
}
